import moment from 'moment';

// =============================================================================
// dateFormat — governed moment format helpers for v2/v3.1.
//
// Four tiers matching the v3.1 `dateFormat.*` contract (see
// `ux/Tasks/configs/designTokens.js:351`). Use these helpers instead of
// hand-writing `moment(x).format('MMM DD, YYYY')` in consumer code so the
// date presentation stays consistent across Campaign Details, Tasks, and
// every future v2 route.
//
//   formatFull      — "Jan 05, 2025 2:30 PM"   · timestamps, activity logs
//   formatShort     — "Jan 05 2:30 PM"         · recent activity, compact timestamps
//   formatDateOnly  — "Jan 05, 2025"           · milestones, due dates, project dates
//   formatCompact   — "01/05"                  · dense tables, sparkline labels, axis ticks
//
// Each helper accepts any moment-parseable input (Date, string, Moment) and
// returns an empty string for null/undefined/invalid input. The empty-string
// fallback is intentional — avoids `Invalid date` placeholders in the UI.
// =============================================================================

export const DATE_FORMAT = {
  full: 'MMM DD, YYYY h:mm A',
  short: 'MMM DD h:mm A',
  dateOnly: 'MMM DD, YYYY',
  compact: 'MM/DD',
};

const safeMoment = (input) => {
  if (input === null || input === undefined) return null;
  const m = moment(input);
  return m.isValid() ? m : null;
};

export const formatFull = (input, fallback = '') => {
  const m = safeMoment(input);
  return m ? m.format(DATE_FORMAT.full) : fallback;
};

export const formatShort = (input, fallback = '') => {
  const m = safeMoment(input);
  return m ? m.format(DATE_FORMAT.short) : fallback;
};

export const formatDateOnly = (input, fallback = '') => {
  const m = safeMoment(input);
  return m ? m.format(DATE_FORMAT.dateOnly) : fallback;
};

export const formatCompact = (input, fallback = '') => {
  const m = safeMoment(input);
  return m ? m.format(DATE_FORMAT.compact) : fallback;
};

// Escape hatch for one-off formats that aren't on the governed ladder.
// Prefer the named helpers above; use this only when a specific tier doesn't
// fit (e.g. 24-hour columns with `HH:mm:ss` that require second precision).
export const formatCustom = (input, pattern, fallback = '') => {
  const m = safeMoment(input);
  return m ? m.format(pattern) : fallback;
};
